import type { MailAttachment } from "@/lib/api";
import type { ComposeAttachment } from "@/mail/composeDraft";

async function blobToBase64(blob: Blob): Promise<string> {
  const buffer = await blob.arrayBuffer();
  const bytes = new Uint8Array(buffer);
  const chunk = 0x8000;
  let binary = "";
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

/**
 * Turn saved draft / message attachment metadata into compose bytes.
 * Edit-draft previously opened with attachments: [] so files looked gone.
 */
export async function hydrateDraftAttachments(
  files: MailAttachment[] | undefined,
  resolveUrl: (file: MailAttachment) => Promise<string | null>,
): Promise<ComposeAttachment[]> {
  if (!Array.isArray(files) || files.length === 0) return [];

  const out: ComposeAttachment[] = [];
  for (const file of files) {
    const filename = (file.filename || "").trim() || "attachment";
    try {
      const href = await resolveUrl(file);
      if (!href) continue;
      const response = await fetch(href);
      if (!response.ok) continue;
      const blob = await response.blob();
      if (!blob.size) continue;
      const contentBase64 = await blobToBase64(blob);
      if (!contentBase64) continue;
      const contentId = (file.contentId || "").trim();
      out.push({
        filename,
        mimeType:
          (file.mimeType || blob.type || "application/octet-stream").trim() ||
          "application/octet-stream",
        contentBase64,
        size: file.size && file.size > 0 ? file.size : blob.size,
        ...(contentId ? { contentId, inline: true } : {}),
      });
    } catch {
      /* Skip one bad file; still hydrate the rest. */
    }
  }
  return out;
}
